import { TagModal } from "@/components/modal/TagModal";
import type { SlideDomainEnum } from "@/generated/graphql";
import { SLIDE_DOMAINS } from "@/lib/constants/slides";
import { useTags } from "@/lib/hooks/tags/useTags";
import { TEMPLATE_LABELS } from "@/lib/slides/slideTemplates";
import { type SlideTemplate, SlideTemplateEnum } from "@ai-teach/types";
import { useEffect, useRef, useState } from "react";
import { MdAdd, MdCheck, MdExpandLess, MdExpandMore } from "react-icons/md";

/** Slides carrying no domain / no tag at all — not a value, so it needs its own sentinel. */
export const NONE = "none";

export type StatusFilter = "incomplete" | "visible" | "hidden";

/** Every filter is independent; `null` means the dimension is not narrowing anything. */
export interface SlideFilterState {
  status: StatusFilter | null;
  template: SlideTemplate | null;
  domain: SlideDomainEnum | typeof NONE | null;
  tagId: string | typeof NONE | null;
}

export const EMPTY_SLIDE_FILTERS: SlideFilterState = {
  status: null,
  template: null,
  domain: null,
  tagId: null,
};

export const hasActiveFilter = (filters: SlideFilterState) =>
  Object.values(filters).some((value) => value !== null);

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "incomplete", label: "Incomplete" },
  { value: "visible", label: "Visible" },
  { value: "hidden", label: "Hidden" },
];

interface Option<T extends string> {
  value: T;
  label: string;
  color?: string;
}

interface FilterDropdownProps<T extends string> {
  label: string;
  /** Shown on the button and as the reset row when nothing is selected. */
  allLabel: string;
  value: T | null;
  options: Option<T>[];
  onChange: (value: T | null) => void;
  className?: string;
  /** Pinned row under the options, for creating a value the list does not offer yet. */
  addAction?: { label: string; onClick: () => void };
}

function FilterDropdown<T extends string>({
  label,
  allLabel,
  value,
  options,
  onChange,
  className = "",
  addAction,
}: FilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((option) => option.value === value);
  const isActive = value !== null;

  const handleSelect = (next: T | null) => {
    onChange(next);
    setIsOpen(false);
  };

  const rowClass = (isSelected: boolean) =>
    `w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-all duration-150 ${
      isSelected
        ? "bg-primary-500/10 text-primary-600 dark:text-primary-400"
        : "text-secondary-200 hover:bg-gray-100 dark:hover:bg-gray-800"
    }`;

  return (
    <div className={className} ref={dropdownRef}>
      <label className="block label mb-1.5 text-gray-500 dark:text-gray-400">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-surface border rounded-lg hover:border-primary-500 dark:hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all duration-200 ${
            isActive
              ? "border-primary-500 text-primary-600 dark:text-primary-400"
              : "border-gray-300 dark:border-gray-700 text-secondary-200"
          }`}
        >
          <span className="flex items-center gap-2 min-w-0">
            {selected?.color && (
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: selected.color }}
              />
            )}
            <span className="label truncate">{selected?.label ?? allLabel}</span>
          </span>
          {isOpen ? (
            <MdExpandLess className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
          ) : (
            <MdExpandMore className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
          )}
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-surface border border-gray-300 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="py-1 max-h-64 overflow-y-auto">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={rowClass(!isActive)}
              >
                <span className="label">{allLabel}</span>
                {!isActive && <MdCheck className="w-4 h-4" />}
              </button>
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={rowClass(isSelected)}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {option.color && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                      <span className="label truncate">{option.label}</span>
                    </span>
                    {isSelected && <MdCheck className="w-4 h-4 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Outside the scroll area so it stays reachable however long the list gets. */}
            {addAction && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  addAction.onClick();
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-left border-t border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-accent-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
              >
                <MdAdd className="w-4 h-4 flex-shrink-0" />
                <span className="label">{addAction.label}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export interface SlideFilterBarProps {
  filters: SlideFilterState;
  setFilters: (filters: SlideFilterState) => void;
  className?: string;
}

export const SlideFilterBar: React.FC<SlideFilterBarProps> = ({
  filters,
  setFilters,
  className = "",
}) => {
  const { tags } = useTags();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const update = (patch: Partial<SlideFilterState>) => setFilters({ ...filters, ...patch });

  const tagOptions: Option<string>[] = [
    { value: NONE, label: "No tag" },
    ...tags.map((tag) => ({ value: tag.id, label: tag.name, color: tag.color })),
  ];

  return (
    <>
      <div className={`flex flex-wrap items-end gap-3 ${className}`}>
        <FilterDropdown
          label="Status"
          allLabel="Any status"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(status) => update({ status })}
          className="w-40"
        />

        {/* Every template, not just the creatable ones — legacy slides still carry the retired ones. */}
        <FilterDropdown
          label="Template"
          allLabel="All templates"
          value={filters.template}
          options={SlideTemplateEnum.options.map((template) => ({
            value: template,
            label: TEMPLATE_LABELS[template],
          }))}
          onChange={(template) => update({ template })}
          className="w-44"
        />

        <FilterDropdown
          label="Domain"
          allLabel="All domains"
          value={filters.domain}
          options={[
            { value: NONE, label: "No domain" },
            ...SLIDE_DOMAINS.map((domain) => ({ value: domain.enum, label: domain.label })),
          ]}
          onChange={(domain) => update({ domain })}
          className="w-44"
        />

        <FilterDropdown
          label="Tag"
          allLabel="All tags"
          value={filters.tagId}
          options={tagOptions}
          onChange={(tagId) => update({ tagId })}
          className="w-44"
          addAction={{ label: "Add new tag", onClick: () => setIsTagModalOpen(true) }}
        />

        {hasActiveFilter(filters) && (
          <button
            type="button"
            onClick={() => setFilters(EMPTY_SLIDE_FILTERS)}
            className="label px-3 py-2.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-150"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Creating from the filter selects the new tag, since filtering by it is the intent. */}
      <TagModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        onSaved={(tag) => update({ tagId: tag.id })}
      />
    </>
  );
};
